import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inject, observer } from 'mobx-react';
import { AuthContext, ToastContext } from '@enter/core';
import { ProviderEvidence } from '@enter/core';
import { IInjectedRootStore } from 'stores';
import { DialogContext } from 'services';
import { EvidenceHeader, EvidenceInfoCards, EvidenceViewSection } from './components';

type IInjectedProps = IInjectedRootStore;

export const EvidenceDetailsOutlet = inject('rootStore')(
    observer((props: {}) => {
        const { rootStore } = props as IInjectedProps;
        const { evidenceId } = useParams<{ evidenceId: string }>();
        const navigate = useNavigate();
        
        if (!rootStore || !evidenceId) {
            return <></>;
        }

        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        const toastContext = useContext(ToastContext);
        
        const [evidence, setEvidence] = useState<ProviderEvidence | undefined>();
        const [loading, setLoading] = useState(false);
        const [isNavigating, setIsNavigating] = useState(false);
        
        const paginationState = rootStore.evidenceStore.paginationState;

        const handleFetchEvidence = useCallback(async () => {
            if (!evidenceId) return;
            setLoading(true);
            try {
                authContext.setGroup?.('initEvidence');
                const fetchedEvidence = await rootStore.evidenceStore.getEvidence(evidenceId);
                setEvidence(fetchedEvidence);
            } catch (error) {
                console.error('Error fetching evidence:', error);
                setEvidence(undefined);
                toastContext.notify?.('Failed to fetch evidence details', 'right');
            } finally {
                setLoading(false);
            }
        }, [evidenceId, rootStore, authContext, toastContext]);

        const copyToClipboard = (text: string, successMessage: string) => {
            navigator.clipboard.writeText(text).then(() => {
                toastContext.notify?.(successMessage, 'right');
            }).catch(() => {
                toastContext.notify?.('Failed to copy to clipboard', 'right');
            });
        };

        const copyEvidenceId = () => {
            if (evidenceId) {
                copyToClipboard(evidenceId, 'Evidence ID copied to clipboard');
            }
        };

        const copyEvidenceUrl = () => {
            const currentUrl = window.location.href;
            copyToClipboard(currentUrl, 'Evidence URL copied to clipboard');
        };

        const handleNavigateToOffset = useCallback(async (newOffset: number) => {
            if (!paginationState) return;
            
            setIsNavigating(true);
            try {
                const result = await rootStore.evidenceStore.fetchOneByOffset(
                    newOffset,
                    paginationState.filters,
                    paginationState.sortRequest,
                );
                
                if (result.evidence?.id) {
                    rootStore.evidenceStore.updatePaginationOffset(newOffset, result.total);
                    navigate(`../${result.evidence.id}`, { relative: 'path' });
                } else {
                    toastContext.notify?.('Evidence not found', 'right');
                }
            } catch (error) {
                console.error('Error navigating to evidence:', error);
                toastContext.notify?.('Failed to navigate to evidence', 'right');
            } finally {
                setIsNavigating(false);
            }
        }, [paginationState, rootStore.evidenceStore, navigate, toastContext]);

        const handlePrevious = useCallback(() => {
            if (paginationState && paginationState.offset > 0) {
                handleNavigateToOffset(paginationState.offset - 1);
            }
        }, [paginationState, handleNavigateToOffset]);

        const handleNext = useCallback(() => {
            if (paginationState && paginationState.offset < paginationState.total - 1) {
                handleNavigateToOffset(paginationState.offset + 1);
            }
        }, [paginationState, handleNavigateToOffset]);

        useEffect(() => {
            handleFetchEvidence();
            
            // Cleanup: cancel group when component unmounts
            return () => {
                authContext.cancelGroup?.('initEvidence');
            };
        }, [handleFetchEvidence, authContext]);

        if (loading) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div>Loading evidence details...</div>
                </div>
            );
        }

        if (!evidence) {
            return (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div>Evidence not found</div>
                </div>
            );
        }

        return (
            <div style={{ padding: '0px 20px 20px' }}>
                <EvidenceHeader
                    evidence={evidence}
                    onCopyEvidenceId={copyEvidenceId}
                    onCopyEvidenceUrl={copyEvidenceUrl}
                    paginationOffset={paginationState?.offset}
                    paginationTotal={paginationState?.total}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                    isNavigating={isNavigating}
                />

                {/* Evidence Information Cards */}
                <EvidenceInfoCards
                    evidence={evidence}
                    dialogContext={dialogContext}
                />

                {/* Evidence View Section (View Only) */}
                <div style={{ marginTop: '20px', width: '100%' }}>
                    <EvidenceViewSection
                        evidence={evidence}
                    />
                </div>
            </div>
        );
    }),
);

